import { CityData, NavNode, NavEdge } from '../types/map';
import { GameState, Player, Agent, AgentState, AgentBelief, ExtractionPoint, Card, Item } from '../types/game';
import { createStarterDeck, createAgentDeck, shuffleDeck, drawHand, CARD_TEMPLATES } from '../data/cards';
import { placeItems, placeExtractions, placeSafehouses, placePassphrases, ITEMS } from '../data/items';
import { tuning } from '../data/tuning';
import { RunState } from '../types/run';
import {
  applyPassivesToDeck,
  getStartingPrepBonus,
  getPlayerHandSize,
} from './passiveEffects';
import { hasPassive } from '../data/passives';
import { generateAgentLoadout } from './agentLoadout';

/**
 * Build the player's starting inventory based on Loadout passives.
 * Each loadout passive contributes one starting item from the ITEMS pool.
 */
function buildStartingInventory(run: RunState | null | undefined): Item[] {
  const items: Item[] = [];
  const makeItem = (key: string) => {
    const template = ITEMS[key];
    if (!template) return null;
    return { ...template, id: `${template.id}-loadout-${Math.random().toString(36).slice(2, 8)}` };
  };
  if (hasPassive(run, 'concealedCarry')) {
    const p = makeItem('pistol'); if (p) items.push(p);
  }
  if (hasPassive(run, 'edgeInTheBoot')) {
    const k = makeItem('combatKnife'); if (k) items.push(k);
  }
  if (hasPassive(run, 'overwatchPackage')) {
    const s = makeItem('sniperRifle'); if (s) items.push(s);
  }
  if (hasPassive(run, 'locksmithsNephew')) {
    const l = makeItem('lockPick'); if (l) items.push(l);
  }
  return items;
}

/**
 * Reset each card's apCost and movementRange to the tuning defaults before
 * passives get re-applied. This keeps passive reductions from compounding
 * across rounds (otherwise Tactician would shave 1 AP off each tactics card
 * every round, not just once). Per-round effects like Field Modify don't
 * persist across rounds by design, so this is safe.
 */
function resetCardCostsToTuning(cards: Card[]): Card[] {
  const nameToKey = new Map<string, string>();
  for (const [key, tpl] of Object.entries(CARD_TEMPLATES)) {
    nameToKey.set(tpl.name, key);
  }
  return cards.map(card => {
    const templateKey = nameToKey.get(card.name);
    if (!templateKey) return card;
    const ct = tuning.cards[templateKey];
    if (!ct) return card;
    return {
      ...card,
      apCost: ct.apCost,
      originalApCost: undefined,
      // isExtractRequired is a per-round marker — by the time this function
      // runs for a new round, the card has already made it through the
      // extraction gate and earned its place in the permanent deck.
      isExtractRequired: false,
      ...(ct.movementRange !== undefined ? { movementRange: ct.movementRange } : {}),
    };
  });
}

// --- Utilities ---

/**
 * Find the largest connected component in the navigation graph.
 * Returns the set of node IDs in that component.
 */
function findLargestComponent(nodes: NavNode[], edges: NavEdge[]): Set<string> {
  const edgeMap = new Map<string, NavEdge>();
  for (const e of edges) edgeMap.set(e.id, e);

  const visited = new Set<string>();
  let largestComponent = new Set<string>();

  for (const node of nodes) {
    if (visited.has(node.id)) continue;

    // BFS from this node
    const component = new Set<string>();
    const queue = [node.id];
    while (queue.length > 0) {
      const currentId = queue.pop()!;
      if (component.has(currentId)) continue;
      component.add(currentId);
      visited.add(currentId);

      const current = nodes.find(n => n.id === currentId);
      if (!current) continue;

      for (const edgeId of current.edges) {
        const edge = edgeMap.get(edgeId);
        if (!edge) continue;
        const neighborId = edge.from === currentId ? edge.to : edge.from;
        if (!component.has(neighborId)) queue.push(neighborId);
      }
    }

    if (component.size > largestComponent.size) {
      largestComponent = component;
    }
  }

  return largestComponent;
}

function haversineDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371000;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// Find a random node in a zone type (residential preferred for player start)
function findRandomNodeInZone(
  nodes: NavNode[],
  edges: Map<string, { zone: string }>,
  preferredZone: string,
  fallbackCenter: { lat: number; lng: number },
): NavNode {
  // Collect nodes that have at least one edge in the preferred zone
  const zoneNodes = nodes.filter(n =>
    n.edges.some(edgeId => edges.get(edgeId)?.zone === preferredZone)
  );

  if (zoneNodes.length > 0) {
    return zoneNodes[Math.floor(Math.random() * zoneNodes.length)];
  }

  // Fallback: pick a random node near center
  const sorted = [...nodes].sort((a, b) => {
    const da = (a.lat - fallbackCenter.lat) ** 2 + (a.lng - fallbackCenter.lng) ** 2;
    const db = (b.lat - fallbackCenter.lat) ** 2 + (b.lng - fallbackCenter.lng) ** 2;
    return da - db;
  });
  // Pick from the inner 30% of nodes (near center)
  const pool = sorted.slice(0, Math.max(10, Math.floor(sorted.length * 0.3)));
  return pool[Math.floor(Math.random() * pool.length)];
}

// --- Initialization ---
// (Agent codenames live in src/engine/agentLoadout.ts now — they're part
// of the procedural loadout that gets rolled per round.)

export function initializeGameState(
  cityData: CityData,
  existingDeck?: Card[],
  runState?: RunState | null,
): GameState {
  const { navigation, pois, center } = cityData;
  const allNodes = navigation.nodes;
  const edgeMap = new Map(navigation.edges.map(e => [e.id, e]));

  // Only use nodes from the largest connected component (avoids isolated areas)
  const mainComponent = findLargestComponent(allNodes, navigation.edges);
  const nodes = allNodes.filter(n => mainComponent.has(n.id));

  // Place extraction points at map edges (before player, so we can enforce distance)
  const extractions = placeExtractions(cityData);

  // Place player in a residential zone, at least 2km from all exits
  const playerNode = findPlayerStart(nodes, edgeMap, center, extractions);

  // Place agent 2-3km from player
  const agentNode = findAgentStart(nodes, playerNode, 2000, 3000);

  // Use existing deck from a previous round (progression) or create fresh starter deck.
  // Reset costs to tuning defaults first (so passive reductions don't compound
  // across rounds), then re-apply passives fresh — this way newly-acquired
  // passives retroactively affect cards the player already owns, and losing
  // a passive (not currently possible, but future-proof) would cleanly revert.
  const shuffled = shuffleDeck(existingDeck ? [...existingDeck] : createStarterDeck());
  const reset = resetCardCostsToTuning(shuffled);
  const deck = applyPassivesToDeck(reset, runState);
  const { hand, remaining } = drawHand(deck, getPlayerHandSize(tuning.handSize, runState));

  const player: Player = {
    position: { lat: playerNode.lat, lng: playerNode.lng },
    nearestNodeId: playerNode.id,
    facing: 0,
    exposure: 0,
    visionConeWidth: 90,
    deck: remaining,
    hand,
    discard: [],
    // Loadout passives: Concealed Carry / Edge in the Boot / Overwatch Package
    // each contribute a starting item to the player's inventory.
    items: buildStartingInventory(runState),
    ap: tuning.playerMaxAp,
    maxAp: tuning.playerMaxAp,
    isInVehicle: false,
    isInTaxi: false,
    taxiRemainingPath: null,
    disguiseTurnsLeft: 0,
    isInTransit: false,
    transitLineId: null,
    transitDirection: null,
    transitStopIndex: 0,
    isInBuilding: false,
    hidingBuildingId: null,
    hidingTurnsLeft: 0,
    instinctTurnsLeft: 0,
    ghostActive: false,
    isElevated: false,
    rooftopPosition: null,
    rooftopBuildingLevels: null,
    detected: false,
    detectedTurnsRemaining: 0,
    seenAgentTurnsLeft: 0,
    combatCooldownTurnsLeft: 0,
    activeBuffs: [],
    // Cool Head passive: start each round with +20% prep already banked.
    prepBonus: getStartingPrepBonus(runState),
    prepBuiltThisTurn: 0,
    adrenalineApThisTurn: 0,
    signalsGeneratedThisTurn: 0,
    // deepCoverActive removed — Deep Cover is now an agent-only passive
    // (see src/data/agentPassives.ts). Phase 2 wires the detection cap.
    freeRunnerActive: hasPassive(runState, 'freeRunner'),
    burnPile: [],
    retainedCardIds: [],
    bonusApNextTurn: 0,
    coldReadActive: false,
    intelChainActive: false,
    doubleTapActive: false,
    contingencyActive: 0,
    operationalIntegrityActive: false,
    cardsPlayedThisTurn: 0,
    sharpenActive: false,
    lastPlayedMovement: false,
    patienceActive: false,
    misdirectActive: false,
    misdirectPath: null,
    misdirectPathTurn: null,
    stageExitPath: null,
    stageExitPathTurn: null,
    trafficAnalysisEdges: null,
    trafficAnalysisRoadName: null,
    radioScannerTurnsLeft: 0,
    jammerTurnsLeft: 0,
    jammerPosition: null,
    jammerRadius: 350,
    isInSafehouse: false,
    safehouseTurnsLeft: 0,
    safehousePosition: null,
    coldExitTurnsLeft: 0,
    hardTargetActive: hasPassive(runState, 'hardTarget'),
    hardTargetUsedThisRound: false,
    echoChamberActive: hasPassive(runState, 'echoChamber'),
    blownEntranceActive: hasPassive(runState, 'blownEntrance'),
    doubleAgentPrimed: false,
    lowProfileActive: false,
    paradeRoutePrimed: false,
    stopAndFriskPrimed: false,
    overwatch: null,
    staticActive: false,
    deadMansSwitch: null,
    doppelgangerRevealTurnsLeft: 0,
  };

  // --- Generate the procedural agent loadout for this round ---
  // Rolls weapon, defensive items, passives, face, codename, and deck
  // archetype based on the current run round. See agentLoadout.ts for
  // the scaling tables. Loadout is stored on the Agent so the Target
  // dossier panel can render it and so Phase-2 hooks can read it.
  //
  // If the current map node has a pre-rolled loadout, use it — this keeps
  // the map-preview tooltip honest (what you saw in Act 1 is what you
  // fight) and locks passive counts per node type (Hit = 0, Elite/Boss = 2).
  // Practice mode and legacy saves fall through to a fresh roll.
  const preRolled = (() => {
    const map = runState?.runMap;
    const id = runState?.currentNodeId;
    if (!map || !id) return null;
    const node = map.nodes.find(n => n.id === id);
    return (node?.agentLoadout as import('../types/game').AgentLoadout | null | undefined) ?? null;
  })();
  const loadout = preRolled ?? generateAgentLoadout(runState?.round ?? 1, runState?.usedCodenames ?? []);

  // Create agent deck from the loadout's rolled archetype
  const agentDeck = shuffleDeck(createAgentDeck(loadout.archetype));
  const { hand: agentHand, remaining: agentRemaining } = drawHand(agentDeck, tuning.agentCardSystem.handSize);

  // Initialize belief centered on map center (agent has no info on player initially)
  const initialBelief: AgentBelief = {
    position: { lat: center.lat, lng: center.lng },
    confidence: 0.0,
    radius: tuning.agentBelief.maxRadius,
    lastUpdated: 0,
    clearedAreas: [],
    signalHistory: [],
    inferredHeading: null,
    inferredSpeed: null,
  };

  // Build the agent's equipment array from the loadout. Primary weapon
  // comes from loadout.weaponId (null = unarmed) and defensive items
  // come from loadout.defensiveItemIds which reference ITEMS catalog keys.
  const equipment: Item[] = [];
  if (loadout.weaponId === 'pistol') {
    equipment.push({
      id: 'pistol-agent',
      name: 'Pistol',
      category: 'weapon',
      apCost: 1,
      description: 'Standard issue sidearm.',
      range: 'short',
    });
  } else if (loadout.weaponId === 'sniper-rifle') {
    equipment.push({
      id: 'sniper-rifle-agent',
      name: 'Sniper Rifle',
      category: 'weapon',
      apCost: 2,
      description: 'Long-range precision weapon.',
      range: 'long',
    });
  } else if (loadout.weaponId === 'combat-knife') {
    equipment.push({
      id: 'combat-knife-agent',
      name: 'Combat Knife',
      category: 'weapon',
      apCost: 1,
      description: 'Melee sidearm.',
      range: 'melee',
    });
  }
  // Defensive items — flashbang, bulletproof-vest, etc. Pull from ITEMS
  // and tag with '-agent' suffix so they don't collide with player item IDs.
  for (const itemKey of loadout.defensiveItemIds) {
    // ITEMS is keyed by camelCase internal name; defensiveItemIds uses the
    // kebab-case runtime id. Look up by id field.
    const template = Object.values(ITEMS).find(i => i.id === itemKey);
    if (template) {
      equipment.push({ ...template, id: `${template.id}-agent` });
    }
  }

  const agent: Agent = {
    id: 'agent-1',
    codename: loadout.codename,
    archetype: loadout.archetype,
    loadout,
    state: 'search' as AgentState,
    position: { lat: agentNode.lat, lng: agentNode.lng },
    facing: Math.random() * 360,
    nearestNodeId: agentNode.id,
    equipment,
    abilities: ['Signal Detection', 'Pursuit'],
    weakness: 'Predictable patrol pattern',
    signalsReceived: [],
    // Belief system
    belief: initialBelief,
    // Card system
    deck: agentRemaining,
    hand: agentHand,
    discard: [],
    ap: tuning.agentCardSystem.baseAp,
    maxAp: tuning.agentCardSystem.baseAp,
    prepBonus: 0,
    cardsPlayedThisTurn: 0,
    // AI state
    exposure: 0,
    turnsInCurrentState: 0,
    visitedNodes: [agentNode.id],
    // Cold Opening passive: reveal the agent's vision cone — but NOT their
    // exact position marker — to the player on turn 1 only. Uses the
    // cone-only reveal timer so the player gets a directional hint without
    // the big red dot giving away precise coordinates.
    detectedTurnsRemaining: 0,
    coneOnlyRevealTurnsLeft: hasPassive(runState, 'coldOpening') ? 1 : 0,
    patrolBearing: Math.random() * 2 * Math.PI,
    committedTarget: null,
    beliefSearchPath: [],
    beliefSearchCenter: null,
    beliefSearchRadius: 0,
    instinctActive: false,
    knownPlayerTransit: null,
    isOnTransit: false,
    transitLineId: null,
    transitDirection: null,
    transitStopIndex: 0,
    // Sniper overwatch (Phase 3)
    isElevated: false,
    rooftopPosition: null,
    overwatchBuildingIdx: null,
    // Extraction-op kill state — agent starts alive in every round.
    isDead: false,
  };

  // Place items at POIs. runState is passed through so Contacts passives
  // (Quartermaster, Gadget Guy, Locksmith's Nephew, Black Market Connect)
  // can bias loot-table rolls and pickup density.
  const placedItems = placeItems(pois, 0.01, runState);
  const safehouses = placeSafehouses(cityData);
  const passphrases = placePassphrases(pois);
  // Cryptologist: start the round with one of the two required passphrases
  // already collected (the first placed one).
  if (hasPassive(runState, 'cryptologist') && passphrases.length > 0) {
    passphrases[0] = { ...passphrases[0], collected: true };
  }

  return {
    turn: 1,
    phase: 'observe',
    player,
    agent,
    signals: [],
    placedItems,
    extractions,
    safehouses,
    passphrases,
    wireTaps: [],
    acousticPings: [],
    bearings: [],
    reconStrips: [],
    traceRouteTrail: null,
    doppelganger: null,
    districtScanName: null,
    districtScanTurn: null,
    informantTips: [],
    spotters: [],
    stakeouts: [],
    surveillanceVans: [],
    // Target dossier starts hidden — flips true when the player enters
    // their first safehouse this round.
    targetRevealed: false,
  };
}

function findPlayerStart(
  nodes: NavNode[],
  edgeMap: Map<string, NavEdge>,
  center: { lat: number; lng: number },
  extractions: ExtractionPoint[],
): NavNode {
  const MIN_EXIT_DIST = 2000; // 2km minimum from any exit

  // Get residential candidates near center
  const residentialCandidates = nodes.filter(n => {
    // Must be connected to a residential edge
    const nearEdge = [...edgeMap.values()].find(e =>
      (e.from === n.id || e.to === n.id) && e.zone === 'residential'
    );
    if (!nearEdge) return false;

    // Must be at least 2km from all exits
    const farEnough = extractions.every(ext =>
      haversineDistance(n.lat, n.lng, ext.lat, ext.lng) >= MIN_EXIT_DIST
    );
    return farEnough;
  });

  if (residentialCandidates.length > 0) {
    // Prefer nodes near center
    const sorted = residentialCandidates.sort((a, b) => {
      const da = haversineDistance(a.lat, a.lng, center.lat, center.lng);
      const db = haversineDistance(b.lat, b.lng, center.lat, center.lng);
      return da - db;
    });
    const pool = sorted.slice(0, Math.max(10, Math.floor(sorted.length * 0.3)));
    return pool[Math.floor(Math.random() * pool.length)];
  }

  // Fallback: use original logic if no node satisfies distance constraint
  return findRandomNodeInZone(nodes, edgeMap, 'residential', center);
}

function findAgentStart(
  nodes: NavNode[],
  playerNode: NavNode,
  minDist: number,
  maxDist: number,
): NavNode {
  const candidates = nodes.filter(n => {
    const d = haversineDistance(playerNode.lat, playerNode.lng, n.lat, n.lng);
    return d >= minDist && d <= maxDist;
  });

  if (candidates.length > 0) {
    return candidates[Math.floor(Math.random() * candidates.length)];
  }

  // Fallback: just pick the node closest to the target distance
  const targetDist = (minDist + maxDist) / 2;
  const sorted = [...nodes].sort((a, b) => {
    const da = Math.abs(haversineDistance(playerNode.lat, playerNode.lng, a.lat, a.lng) - targetDist);
    const db = Math.abs(haversineDistance(playerNode.lat, playerNode.lng, b.lat, b.lng) - targetDist);
    return da - db;
  });
  return sorted[0];
}
